import { ChangeEvent, KeyboardEvent, useState } from 'react'
import { useIntl } from 'react-intl'
import { Tooltip } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import RefreshIcon from '@mui/icons-material/Refresh'
import { PageArea } from '..'
import { Text, Title } from '../../components/typography'
import { colorPalette } from '../../style/colorPalette'
import { AiAgentMessageList } from '../../features/AiAgentChat/AiAgentMessageList'
import { useAiAgentConversation } from '../../features/AiAgentChat/hooks/useAiAgentConversation'
import {
  ChatCard,
  ChatColumn,
  ChatHeader,
  ChatInput,
  HeaderIconButton,
  HeaderTexts,
  InlineError,
  InputArea,
  SendButton,
} from './styles'

export const AiAgentChatPage = () => {
  const intl = useIntl()
  const [inputValue, setInputValue] = useState('')
  const { messages, isStreaming, inlineError, sendMessage, resetConversation } =
    useAiAgentConversation({
      greetingText: intl.formatMessage({ id: 'aiAgentChat.greeting' }),
      busyErrorText: intl.formatMessage({ id: 'aiAgentChat.error.busy' }),
      generalErrorText: intl.formatMessage({ id: 'aiAgentChat.error.general' }),
      streamErrorText: intl.formatMessage({
        id: 'aiAgentChat.error.streaming',
      }),
    })

  const handleSendMessage = () => {
    if (isStreaming || inputValue.trim() === '') {
      return
    }
    const messageText = inputValue
    setInputValue('')
    // チャット専用ページには周辺文脈（プロジェクト・WBS等）が存在しないため screen のみ送る。
    sendMessage(messageText, { screen: 'chat' })
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <PageArea>
      <ChatColumn>
        <ChatCard>
          <ChatHeader>
            <HeaderTexts>
              <Title variant="l">
                {intl.formatMessage({ id: 'aiAgentChat.title' })}
              </Title>
              <Text variant="2s" color={colorPalette.monotone[5]}>
                {intl.formatMessage({ id: 'aiAgentChat.description' })}
              </Text>
            </HeaderTexts>
            <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.reset' })}>
              <span>
                <HeaderIconButton
                  size="small"
                  onClick={resetConversation}
                  disabled={isStreaming}
                >
                  <RefreshIcon fontSize="small" />
                </HeaderIconButton>
              </span>
            </Tooltip>
          </ChatHeader>
          <AiAgentMessageList messages={messages} isStreaming={isStreaming} />
          {inlineError && <InlineError>{inlineError}</InlineError>}
          <InputArea>
            <ChatInput
              placeholder={intl.formatMessage({
                id: 'aiAgentChat.input.placeholder',
              })}
              fullWidth={true}
              multiline={true}
              maxRows={6}
              inputProps={{
                'aria-label': intl.formatMessage({
                  id: 'aiAgentChat.input.placeholder',
                }),
              }}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
            />
            <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.send' })}>
              <span>
                <SendButton
                  onClick={handleSendMessage}
                  disabled={isStreaming || inputValue.trim() === ''}
                >
                  <SendIcon />
                </SendButton>
              </span>
            </Tooltip>
          </InputArea>
        </ChatCard>
      </ChatColumn>
    </PageArea>
  )
}
